/**
 * Source registry — defines built-in data sources with metadata,
 * endpoints, update intervals, and required API keys.
 */

import type { SourceCategory } from '@/sources/base-source';

export interface SourceDef {
  id: string;
  name: string;
  description: string;
  category: SourceCategory;
  updateIntervalMs: number;
  requiresApiKey: boolean;
  apiKeyEnv?: string;
  apiKeyUrl?: string;
  endpoint?: string;
  dataLicense: string;
}

export const SOURCE_REGISTRY: SourceDef[] = [
  {
    id: 'acled',
    name: 'ACLED',
    description: 'Armed Conflict Location & Event Data — conflict, protests, political violence',
    category: 'conflict',
    updateIntervalMs: 3600_000,
    requiresApiKey: true,
    apiKeyEnv: 'VITE_ACLED_PASSWORD',
    apiKeyUrl: 'https://acleddata.com/myacled',
    endpoint: 'https://acleddata.com/api/acled/read',
    dataLicense: 'ACLED Terms of Use',
  },
  {
    id: 'gdelt',
    name: 'GDELT',
    description: 'Global Database of Events, Language, and Tone',
    category: 'news',
    updateIntervalMs: 900_000,
    requiresApiKey: false,
    endpoint: 'https://api.gdeltproject.org/api/v2/doc/doc',
    dataLicense: 'CC BY 4.0',
  },
  {
    id: 'firms',
    name: 'NASA FIRMS',
    description: 'Fire Information for Resource Management System — satellite fire detection',
    category: 'environmental',
    updateIntervalMs: 3600_000,
    requiresApiKey: false,
    endpoint: 'https://firms.modaps.eosdis.nasa.gov/api',
    dataLicense: 'Public Domain',
  },
  {
    id: 'usgs',
    name: 'USGS Earthquakes',
    description: 'US Geological Survey earthquake data',
    category: 'environmental',
    updateIntervalMs: 300_000,
    requiresApiKey: false,
    endpoint: 'https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary',
    dataLicense: 'Public Domain',
  },
  {
    id: 'gdacs',
    name: 'GDACS',
    description: 'Global Disaster Alerting Coordination System — UN disaster alerts',
    category: 'environmental',
    updateIntervalMs: 1800_000,
    requiresApiKey: false,
    endpoint: 'https://www.gdacs.org/xml/rss.xml',
    dataLicense: 'Public Domain',
  },
  {
    id: 'opensky',
    name: 'OpenSky Network',
    description: 'Live aircraft positions via ADS-B receivers',
    category: 'aviation',
    updateIntervalMs: 10_000,
    requiresApiKey: false,
    apiKeyUrl: 'https://opensky-network.org/index.php?option=com_users&view=registration',
    endpoint: 'https://opensky-network.org/api/states/all',
    dataLicense: 'CC BY-NC 4.0',
  },
  {
    id: 'vesselfinder',
    name: 'VesselFinder',
    description: 'Live vessel positions via AIS data',
    category: 'maritime',
    updateIntervalMs: 60_000,
    requiresApiKey: true,
    apiKeyEnv: 'VITE_VESSELFINDER_API_KEY',
    apiKeyUrl: 'https://www.vesselfinder.com/api',
    dataLicense: 'VesselFinder API Terms',
  },
  {
    id: 'sanctions',
    name: 'Sanctions Lists',
    description: 'OFAC, EU, UN consolidated sanctions lists',
    category: 'government',
    updateIntervalMs: 86400_000,
    requiresApiKey: false,
    dataLicense: 'Public Domain',
  },
  // --- New sources (Phase 1 config expansion) ---
  {
    id: 'telegram',
    name: 'Telegram OSINT',
    description: 'Curated OSINT Telegram channel aggregation (27 channels)',
    category: 'news',
    updateIntervalMs: 120_000,
    requiresApiKey: true,
    apiKeyEnv: 'VITE_TELEGRAM_BOT_TOKEN',
    apiKeyUrl: 'https://core.telegram.org/bots#how-do-i-create-a-bot',
    endpoint: 'https://api.telegram.org',
    dataLicense: 'Telegram Terms',
  },
  {
    id: 'oref',
    name: 'OREF (Israeli Sirens)',
    description: 'Israeli Home Front Command real-time siren alerts',
    category: 'conflict',
    updateIntervalMs: 5_000,
    requiresApiKey: false,
    endpoint: 'https://www.oref.org.il/WarningMessages/alert/alerts.json',
    dataLicense: 'Public',
  },
  {
    id: 'gpsjam',
    name: 'GPSJam',
    description: 'GPS/GNSS interference detection via ADS-B analysis',
    category: 'infrastructure',
    updateIntervalMs: 300_000,
    requiresApiKey: false,
    endpoint: 'https://gpsjam.org/api',
    dataLicense: 'GPSJam Terms',
  },
  {
    id: 'cyber',
    name: 'Cyber Threat Feeds',
    description: 'Aggregated cyber threat intelligence — Feodo, URLhaus, OTX, CISA KEV',
    category: 'cyber',
    updateIntervalMs: 300_000,
    requiresApiKey: false,
    dataLicense: 'CC0 1.0 / Various',
  },
  {
    id: 'ucdp',
    name: 'UCDP',
    description: 'Uppsala Conflict Data Program — armed conflict events',
    category: 'conflict',
    updateIntervalMs: 3600_000,
    requiresApiKey: false,
    endpoint: 'https://ucdpapi.pcr.uu.se/api/gedevents',
    dataLicense: 'CC BY 4.0',
  },
  {
    id: 'eonet',
    name: 'NASA EONET',
    description: 'Earth Observatory Natural Event Tracker — wildfires, storms, volcanic eruptions',
    category: 'environmental',
    updateIntervalMs: 1800_000,
    requiresApiKey: false,
    endpoint: 'https://eonet.gsfc.nasa.gov/api/v3/events',
    dataLicense: 'Public Domain',
  },
  {
    id: 'weather',
    name: 'NWS Weather Alerts',
    description: 'US National Weather Service severe weather alerts',
    category: 'environmental',
    updateIntervalMs: 300_000,
    requiresApiKey: false,
    endpoint: 'https://api.weather.gov/alerts/active',
    dataLicense: 'Public Domain',
  },
  {
    id: 'advisory',
    name: 'Travel Advisories',
    description: 'Government travel and security advisory aggregation (27 feeds)',
    category: 'government',
    updateIntervalMs: 3600_000,
    requiresApiKey: false,
    dataLicense: 'Public Domain / Government',
  },
  {
    id: 'outages',
    name: 'Internet Outages',
    description: 'Cloudflare Radar internet outage detection',
    category: 'infrastructure',
    updateIntervalMs: 300_000,
    requiresApiKey: false,
    endpoint: 'https://radar.cloudflare.com/api',
    dataLicense: 'Cloudflare Terms',
  },
  {
    id: 'cable-health',
    name: 'Cable Health',
    description: 'Submarine cable status monitoring and fault advisories',
    category: 'infrastructure',
    updateIntervalMs: 1800_000,
    requiresApiKey: false,
    dataLicense: 'Various',
  },
  {
    id: 'ais-ws',
    name: 'AIS WebSocket',
    description: 'Real-time AIS vessel position stream',
    category: 'maritime',
    updateIntervalMs: 5_000,
    requiresApiKey: true,
    apiKeyEnv: 'VITE_AIS_API_KEY',
    dataLicense: 'AIS Provider Terms',
  },
  {
    id: 'military-bases',
    name: 'Military Bases',
    description: 'Static military base locations from built-in config (220+ bases)',
    category: 'government',
    updateIntervalMs: 86400_000,
    requiresApiKey: false,
    dataLicense: 'Public Domain (OSINT)',
  },
  // --- Phase D: 3 new sources ---
  {
    id: 'fred',
    name: 'FRED Economic Data',
    description: 'Federal Reserve economic indicators — GDP, unemployment, CPI, interest rates',
    category: 'finance',
    updateIntervalMs: 3600_000,
    requiresApiKey: true,
    apiKeyEnv: 'FRED_API_KEY',
    apiKeyUrl: 'https://fred.stlouisfed.org/docs/api/api_key.html',
    endpoint: 'https://api.stlouisfed.org/fred/series/observations',
    dataLicense: 'Public Domain',
  },
  {
    id: 'displacement',
    name: 'UNHCR Displacement',
    description: 'UNHCR refugee and internally displaced persons statistics',
    category: 'social',
    updateIntervalMs: 86400_000,
    requiresApiKey: false,
    endpoint: 'https://api.unhcr.org/population/v1/population/',
    dataLicense: 'CC BY 4.0',
  },
  {
    id: 'airport-delays',
    name: 'Airport Delays',
    description: 'Airport delay and disruption tracking for 128 configured airports',
    category: 'aviation',
    updateIntervalMs: 300_000,
    requiresApiKey: false,
    endpoint: 'https://nasstatus.faa.gov/api/airport-status-information',
    dataLicense: 'Public Domain',
  },
  // --- Session 4: USNI Fleet Tracker ---
  {
    id: 'usni',
    name: 'USNI Fleet Tracker',
    description: 'US Naval Institute Fleet Tracker — carrier strike group deployment positions',
    category: 'maritime',
    updateIntervalMs: 21_600_000,
    requiresApiKey: false,
    endpoint: 'https://news.usni.org/category/fleet-tracker/feed',
    dataLicense: 'USNI News (Fair Use)',
  },
  // --- Session 7: Aircraft enrichment ---
  {
    id: 'wingbits',
    name: 'Aircraft Enrichment',
    description: 'Aircraft metadata enrichment — manufacturer, owner, military classification via ICAO24 lookup',
    category: 'aviation',
    updateIntervalMs: 86400_000,
    requiresApiKey: false,
    endpoint: 'https://opensky-network.org/api/metadata/aircraft/icao24',
    dataLicense: 'CC BY-NC 4.0',
  },
  // --- Phase E: Market data ---
  {
    id: 'market',
    name: 'Market Data',
    description: 'Stock indices, commodities, crypto prices (CoinGecko + Finnhub)',
    category: 'finance',
    updateIntervalMs: 300_000,
    requiresApiKey: false,
    apiKeyEnv: 'FINNHUB_API_KEY',
    apiKeyUrl: 'https://finnhub.io',
    dataLicense: 'CoinGecko Terms / Finnhub Terms',
  },
];

/** Get source definition by ID. */
export function getSourceById(id: string): SourceDef | undefined {
  return SOURCE_REGISTRY.find(s => s.id === id);
}

/** Get sources by category. */
export function getSourcesByCategory(category: SourceCategory): SourceDef[] {
  return SOURCE_REGISTRY.filter(s => s.category === category);
}
